import { OptionSwitcherItem } from '../../../shared/interfaces/option-switcher.interface';
import { WooCommerceDatetimeTimeSlotValue } from '../../../core/enums/woo-commerce/api/woo-commerce-api.enum';

export enum PickupOption {
  standard = 'standard',
  schedule = 'schedule'
}

export interface PickupScheduleInfo {
  date: Date | string;
  time: Date | WooCommerceDatetimeTimeSlotValue | string;
}

interface PickupTimeOptionsConfig {
  includeStandard?: boolean;
}

export const PICKUP_TIME_OPTIONS: OptionSwitcherItem[] = [
  {
    id: PickupOption.standard,
    name: 'Standard',
    description: 'In 25 minutes'
  },
  {
    id: PickupOption.schedule,
    name: 'Schedule for later',
    description: 'Schedule time'
  }
];

export function getPickupTimeOptions(
  standardDescription: string,
  schedule?: PickupScheduleInfo,
  config: PickupTimeOptionsConfig = {}
) {
  const includeStandard = config.includeStandard ?? true;
  const options: OptionSwitcherItem[] = PICKUP_TIME_OPTIONS.map((option) => ({
    ...option
  }));

  const standardOption = options.find(
    (option) => option.id === PickupOption.standard
  );
  if (standardOption) {
    standardOption.description = standardDescription
      ? `In ${standardDescription}`
      : 'ASAP';
  }

  const scheduleDescription = getPickupScheduleDescription(schedule);
  const scheduleOption = options.find(
    (option) => option.id === PickupOption.schedule
  );
  if (scheduleDescription != null && scheduleOption) {
    scheduleOption.description = scheduleDescription;
  }

  return includeStandard
    ? options
    : options.filter((option) => option.id !== PickupOption.standard);
}

export function getPickupScheduleDescription(
  schedule?: PickupScheduleInfo
): string | undefined {
  if (schedule == null) {
    return undefined;
  }

  const formattedDate = formatScheduleDate(schedule.date);
  const formattedTime = formatScheduleTime(schedule.time);

  if (!formattedDate && !formattedTime) {
    return undefined;
  }

  if (!formattedDate) {
    return formattedTime;
  }

  if (!formattedTime) {
    return formattedDate;
  }

  return `${formattedDate} @ ${formattedTime}`;
}

function formatScheduleDate(date?: Date | string): string {
  if (!date) {
    return '';
  }

  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
  const monthName = dateObj.toLocaleDateString('en-US', { month: 'long' });
  const day = dateObj.getDate();

  return `${dayName}, ${monthName} ${day}`;
}

function formatScheduleTime(
  time?: Date | WooCommerceDatetimeTimeSlotValue | string
): string {
  if (time === WooCommerceDatetimeTimeSlotValue.Asap) {
    return 'ASAP';
  }

  // Handle Date objects
  if (time instanceof Date) {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${period}`;
  }

  // Handle string - could be a time slot (e.g., "09:00 AM - 10:00 AM") or a date string
  if (typeof time === 'string') {
    // Check if it's an ISO date string (including epoch dates like 1970-01-01T08:10:00.000Z)
    // Match formats like: 1970-01-01T08:10:00 or 1970-01-01T08:10:00.000Z
    const isoDateMatch = time.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/);
    if (isoDateMatch) {
      // It's a date string, parse it and extract time (even if it's epoch date)
      const dateObj = new Date(time);
      if (!isNaN(dateObj.getTime())) {
        // Extract time from the date string - we only care about hours and minutes
        // Even if the date is 1970-01-01 (epoch), we still extract the time correctly
        const hours = dateObj.getHours();
        const minutes = dateObj.getMinutes();
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, '0');
        return `${displayHours}:${displayMinutes} ${period}`;
      }
    }
    // Otherwise, it's likely a time slot string, return as-is
    return time;
  }

  return 'Schedule Time';
}
